// Modelo y reglas de la tabla de Vendedores: filas, alertas, píldoras, filtros y orden. Puro.
import type {
    SellerRole,
    SellerStatus,
    SellersTableRow,
} from '@/api/services/retail-wizard/backoffice/sellersTableContract';
import { daysBetweenMx } from '@/lib/dateHelpers';
import { STORE_MANAGER_ROLE_ID } from './newSellerForm';

/** La fila que pinta la pantalla. `null` es «no hay fuente» y se pinta «—». */
export interface SellerRow {
    sellerId: string;
    /** Clave corta legible: `V-` + el final del id (4 caracteres, más si se repite). */
    shortCode: string;
    name: string;
    status: SellerStatus | null;
    phone: string | null;
    storeId: string | null;
    storeName: string | null;
    zoneName: string | null;
    /** Roles RBAC; `null` si RBAC no respondió. */
    roles: SellerRole[] | null;
    email: string | null;
    /** Alta en el padrón. */
    memberSince: string | null;
    customersServed: number | null;
    salesCount: number | null;
    placedCents: number | null;
    averageTicketCents: number | null;
    lastSaleAt: string | null;
    weekOverWeekChangePct: number | null;
    goalCents: number | null;
    commissionCents: number | null;
    npiM30Pct: number | null;
}

/** Umbrales del mock: caída >20% vs semana anterior y más de 7 días sin venta. */
export const DROP_THRESHOLD_PCT = -20;
export const NO_SALE_DAYS_THRESHOLD = 7;
const TOP_NETWORK_FRACTION = 0.1;
const TOP_PILL_FRACTION = 0.25;

const SHORT_CODE_LENGTH = 4;

export function sellerShortCode(sellerId: string, length = SHORT_CODE_LENGTH): string {
    return `V-${sellerId.slice(-length).toUpperCase()}`;
}

/** Clave de cada fila; solo las que chocan se alargan, lo justo para distinguirse. */
export function withUniqueShortCodes(rows: SellerRow[]): SellerRow[] {
    const codeOf = new Map<string, string>();
    const assign = (ids: string[], length: number) => {
        const groups = new Map<string, string[]>();
        for (const id of ids) {
            const code = sellerShortCode(id, length);
            groups.set(code, [...(groups.get(code) ?? []), id]);
        }
        for (const [code, group] of groups) {
            const exhausted = group.every((id) => id.length <= length);
            if (group.length === 1 || exhausted) group.forEach((id) => codeOf.set(id, code));
            else assign(group, length + 1);
        }
    };
    assign([...new Set(rows.map((row) => row.sellerId))], SHORT_CODE_LENGTH);
    return rows.map((row) => ({ ...row, shortCode: codeOf.get(row.sellerId) ?? row.shortCode }));
}

/** Nombre que se pinta cuando el padrón no tiene uno. */
export const UNNAMED_SELLER = 'Sin nombre';

/** `rbacAvailable` en falso deja los roles en `null`: nunca un rol inventado. */
export function fromTableRow(row: SellersTableRow, rbacAvailable: boolean): SellerRow {
    return {
        sellerId: row.retailUserId,
        shortCode: sellerShortCode(row.retailUserId),
        name: row.name ?? UNNAMED_SELLER,
        status: row.status,
        phone: row.phone,
        storeId: row.storeId,
        storeName: row.storeName,
        zoneName: row.zoneName,
        roles: rbacAvailable ? (row.roles ?? null) : null,
        email: row.email ?? null,
        memberSince: row.memberSince ?? null,
        customersServed: row.customersServed ?? null,
        salesCount: row.salesCount,
        placedCents: row.placedCents,
        averageTicketCents: row.averageTicketCents,
        lastSaleAt: row.lastSaleAt,
        weekOverWeekChangePct: row.weekOverWeekChangePct,
        goalCents: row.goalCents,
        commissionCents: row.commissionCents,
        npiM30Pct: row.npiM30Pct,
    };
}

/** Roles en orden alfabético por nombre. */
export function sortedRoles(roles: readonly SellerRole[]): SellerRole[] {
    return [...roles].sort((a, b) => a.name.localeCompare(b.name, 'es-MX'));
}

/** Si tiene el rol de admin de tienda; `null` si RBAC no respondió. */
export function isStoreAdmin(row: SellerRow): boolean | null {
    return row.roles === null ? null : row.roles.some((role) => role.key === STORE_MANAGER_ROLE_ID);
}

export function isInactive(row: SellerRow): boolean {
    return row.status === 'INACTIVE' || row.status === 'SUSPENDED';
}

/** Quienes colocaron algo, de mayor a menor colocación. */
function placedRanking(rows: readonly SellerRow[]): SellerRow[] {
    return rows
        .filter((row) => (row.placedCents ?? 0) > 0)
        .sort((a, b) => (b.placedCents ?? 0) - (a.placedCents ?? 0));
}

/** Lugar en la red por colocación (1 = el mejor); sin colocación no tiene lugar. */
export function networkRanks(rows: readonly SellerRow[]): Map<string, number> {
    return new Map(placedRanking(rows).map((row, index) => [row.sellerId, index + 1]));
}

/** El primer `fraction` de los que colocaron, al menos uno si alguien colocó. */
function topIds(rows: readonly SellerRow[], fraction: number): Set<string> {
    const ranked = placedRanking(rows);
    const size = Math.ceil(ranked.length * fraction);
    return new Set(ranked.slice(0, size).map((row) => row.sellerId));
}

/** Días desde la última venta; `null` si no se sabe. */
export function daysWithoutSale(row: SellerRow, now: Date = new Date()): number | null {
    return row.lastSaleAt === null ? null : daysBetweenMx(row.lastSaleAt, now);
}

export interface SellerAlerts {
    topNetwork: boolean;
    /** Días sin venta cuando pasan del umbral; `null` si no aplica. */
    noSaleDays: number | null;
    drop: boolean;
}

export interface SellerContext {
    ranks: Map<string, number>;
    top10: Set<string>;
    top25: Set<string>;
    now: Date;
}

export function buildContext(rows: readonly SellerRow[], now: Date = new Date()): SellerContext {
    return {
        ranks: networkRanks(rows),
        top10: topIds(rows, TOP_NETWORK_FRACTION),
        top25: topIds(rows, TOP_PILL_FRACTION),
        now,
    };
}

export function sellerAlerts(row: SellerRow, context: SellerContext): SellerAlerts {
    const days = daysWithoutSale(row, context.now);
    const active = !isInactive(row);
    return {
        topNetwork: context.top10.has(row.sellerId),
        noSaleDays: active && days !== null && days > NO_SALE_DAYS_THRESHOLD ? days : null,
        drop:
            active &&
            row.weekOverWeekChangePct !== null &&
            row.weekOverWeekChangePct < DROP_THRESHOLD_PCT,
    };
}

export function needsAttention(alerts: SellerAlerts): boolean {
    return alerts.noSaleDays !== null || alerts.drop;
}

/** Cumplimiento contra la meta en pesos, como fracción; `null` sin meta. */
export function goalProgress(row: SellerRow): number | null {
    if (row.goalCents === null || row.goalCents <= 0 || row.placedCents === null) return null;
    return row.placedCents / row.goalCents;
}

export type SellerPill = 'all' | 'active' | 'top' | 'attention' | 'storeAdmin';

export interface SellersFilters {
    query: string;
    storeId: string;
    zones: string[];
    /** `key` de los roles elegidos; la fila pasa si tiene alguno. */
    roleKeys: string[];
    pill: SellerPill;
}

export const EMPTY_SELLERS_FILTERS: SellersFilters = {
    query: '',
    storeId: '',
    zones: [],
    roleKeys: [],
    pill: 'all',
};

/** Filtros activos de la columna; la píldora no cuenta porque vive en la barra de la lista. */
export function countActiveFilters(filters: SellersFilters): number {
    const fields = [filters.query.trim(), filters.storeId].filter((v) => v !== '').length;
    return fields + filters.zones.length + filters.roleKeys.length;
}

function matchesPill(row: SellerRow, pill: SellerPill, context: SellerContext): boolean {
    switch (pill) {
        case 'all':
            return true;
        case 'active':
            return row.status === 'ACTIVE';
        case 'top':
            return context.top25.has(row.sellerId);
        case 'attention':
            return needsAttention(sellerAlerts(row, context));
        case 'storeAdmin':
            return isStoreAdmin(row) === true;
    }
}

export function filterSellers(
    rows: readonly SellerRow[],
    filters: SellersFilters,
    context: SellerContext,
): SellerRow[] {
    const query = filters.query.trim().toLowerCase();
    const digits = query.replace(/\D/g, '');
    return rows.filter((row) => {
        if (query) {
            const haystack = `${row.name} ${row.shortCode} ${row.sellerId}`.toLowerCase();
            const phoneHit = digits.length >= 3 && (row.phone ?? '').includes(digits);
            if (!haystack.includes(query) && !phoneHit) return false;
        }
        if (filters.storeId && row.storeId !== filters.storeId) return false;
        if (
            filters.zones.length > 0 &&
            (row.zoneName === null || !filters.zones.includes(row.zoneName))
        )
            return false;
        if (
            filters.roleKeys.length > 0 &&
            !(row.roles ?? []).some((role) => filters.roleKeys.includes(role.key))
        )
            return false;
        return matchesPill(row, filters.pill, context);
    });
}

export function pillCounts(rows: readonly SellerRow[], context: SellerContext) {
    const count = (pill: SellerPill) =>
        rows.filter((row) => matchesPill(row, pill, context)).length;
    return {
        all: rows.length,
        active: count('active'),
        top: count('top'),
        attention: count('attention'),
        storeAdmin: count('storeAdmin'),
    };
}

export type SellerSortKey =
    | 'name'
    | 'role'
    | 'store'
    | 'placed'
    | 'weekOverWeek'
    | 'sales'
    | 'goal';

export interface SellerSort {
    key: SellerSortKey;
    direction: 'asc' | 'desc';
}

export const DEFAULT_SELLER_SORT: SellerSort = { key: 'placed', direction: 'desc' };

const TEXT_SORT_KEYS: ReadonlySet<SellerSortKey> = new Set(['name', 'role', 'store']);

/** Mismo encabezado invierte el sentido; uno nuevo arranca asc en texto y desc en cifras. */
export function toggleSort(current: SellerSort, key: SellerSortKey): SellerSort {
    if (current.key === key) {
        return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
    }
    return { key, direction: TEXT_SORT_KEYS.has(key) ? 'asc' : 'desc' };
}

function sortValue(row: SellerRow, key: SellerSortKey): string | number | null {
    switch (key) {
        case 'name':
            return row.name;
        case 'role':
            return row.roles === null
                ? null
                : sortedRoles(row.roles)
                      .map((role) => role.name)
                      .join(', ');
        case 'store':
            return row.storeName;
        case 'placed':
            return row.placedCents;
        case 'weekOverWeek':
            return row.weekOverWeekChangePct;
        case 'sales':
            return row.salesCount;
        case 'goal':
            return goalProgress(row);
    }
}

/** Ordena por la columna pedida; los `null` siempre al final y el nombre desempata. */
export function sortSellers(rows: readonly SellerRow[], sort: SellerSort): SellerRow[] {
    const sign = sort.direction === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
        const av = sortValue(a, sort.key);
        const bv = sortValue(b, sort.key);
        if (av === null && bv !== null) return 1;
        if (bv === null && av !== null) return -1;
        if (av !== null && bv !== null && av !== bv) {
            const cmp =
                typeof av === 'string' && typeof bv === 'string'
                    ? av.localeCompare(bv, 'es-MX')
                    : Number(av) - Number(bv);
            if (cmp !== 0) return cmp * sign;
        }
        return a.name.localeCompare(b.name, 'es-MX');
    });
}

/** Opciones del combo de tienda, sin repetir y en orden alfabético. */
export function storeOptions(rows: readonly SellerRow[]): { id: string; name: string }[] {
    const byId = new Map<string, string>();
    for (const row of rows) {
        if (row.storeId !== null) byId.set(row.storeId, row.storeName ?? row.storeId);
    }
    return [...byId.entries()]
        .map(([id, name]) => ({ id, name }))
        .sort((a, b) => a.name.localeCompare(b.name, 'es-MX'));
}

/** Zonas presentes en las filas, sin repetir y en orden alfabético. */
export function zoneOptions(rows: readonly SellerRow[]): string[] {
    const zones = new Set<string>();
    for (const row of rows) if (row.zoneName !== null) zones.add(row.zoneName);
    return [...zones].sort((a, b) => a.localeCompare(b, 'es-MX'));
}

/** Roles presentes en las filas, sin repetir y en orden alfabético por nombre. */
export function roleOptions(rows: readonly SellerRow[]): SellerRole[] {
    const byKey = new Map<string, SellerRole>();
    for (const row of rows) for (const role of row.roles ?? []) byKey.set(role.key, role);
    return sortedRoles([...byKey.values()]);
}
